import moment from 'moment';
import { readDate } from '../../utils/appDate';
import { COPY } from './orders.copy';

/**
 * The orders screen's view model.
 *
 * Ported from the Flutter app's `OrdersView.build` (lib/view/components/orders/
 * orders_filters.dart), minus the shift dimension. No React in this file: the
 * screen calls `buildOrdersView` once per render and then only describes the
 * result.
 *
 * The ORDER OF OPERATIONS is deliberate and load-bearing:
 *
 *   1. wrap every order once           -> toEntry
 *   2. derive the waiter options       -> from the DATE-scoped set only
 *   3. narrow by waiter
 *   4. narrow by search
 *   5. narrow by status chip
 *
 * Deriving waiter options before any narrowing is what stops the picker
 * collapsing to the single name you just selected.
 */

// ── Status filter ──────────────────────────────────────────────────────────

export const StatusFilter = {
  ALL: 'all',
  PENDING: 'pending',
  PAID: 'paid',
  INVOICE: 'invoice',
};

/** Declaration order is the on-screen chip order. */
export const STATUS_FILTERS = [
  { id: StatusFilter.ALL, label: COPY.all, dot: null },
  { id: StatusFilter.PENDING, label: COPY.pending, dot: 'pending' },
  { id: StatusFilter.PAID, label: COPY.paid, dot: 'paid' },
  { id: StatusFilter.INVOICE, label: COPY.invoice, dot: 'invoice' },
];

// ── Entry ──────────────────────────────────────────────────────────────────

const toNumber = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

/** A populated ref comes back as an object; an unpopulated one as a bare id. */
const refId = (value) => {
  if (!value) return null;
  if (typeof value === 'string') return value;
  const id = value._id || value.id;
  return id ? String(id) : null;
};

const refName = (value) =>
  value && typeof value === 'object' && value.name ? String(value.name).trim() : '';

/**
 * Wraps one order with everything the list needs, resolved exactly once, so
 * sorting, grouping and rendering never re-parse the same date.
 */
export function toEntry(order) {
  // Read on the shop's clock, not the browser's — see utils/appDate. The
  // instant is the same either way, so sorting is unaffected; what changes is
  // the day this formats as, which is what `dayKey` below groups on.
  const time = readDate(order.date || order.created_at);
  const isInvoice = order.paymentType === 'invoice';
  const items = Array.isArray(order.items) ? order.items : [];

  return {
    order,
    id: String(order._id || order.id),
    orderNumber: order.orderNumber,
    time: time.isValid() ? time : readDate(),

    /**
     * Business day, not calendar day. A night shift running past midnight
     * keeps all of its orders under one heading — the server stamps
     * `shiftDate` for that, and we fall back to the calendar date.
     */
    dayKey: order.shiftDate || (time.isValid() ? time : readDate()).format('YYYY-MM-DD'),

    isInvoice,
    isPending: String(order.orderStatus || '').toLowerCase() === 'pending',
    isCanceled: String(order.orderStatus || '').toLowerCase() === 'canceled',

    total: toNumber(order.total),
    grossTotal: toNumber(order.grossTotal),
    discount: toNumber(order.discount),

    /**
     * VAT, as the server worked it out and stored it.
     *
     * `total` is unchanged — gross minus discount — because every report and
     * every customer balance is built on it. `totalWithVat` is what the
     * customer actually pays, and it falls back to `total` for every order
     * taken before VAT was switched on.
     */
    /**
     * Which account the money went into, as the cashier picked it.
     *
     * A plain label — "Cash", "EVC Plus" — and empty on every order settled
     * before this existed, which the band shows as Unassigned rather than
     * pretending it was cash.
     */
    paymentAccount: String(order.paymentAccount || '').trim(),

    vatRate: toNumber(order.vatRate),
    vatAmount: toNumber(order.vatAmount),
    totalWithVat: order.totalWithVat === undefined || order.totalWithVat === null
      ? toNumber(order.total)
      : toNumber(order.totalWithVat),

    staffId: refId(order.user),
    staffName: refName(order.user),
    customerName: isInvoice ? refName(order.customer) : '',

    itemCount: items.reduce((sum, i) => sum + toNumber(i.quantity), 0),
  };
}

/** Which chip an entry belongs under. The four are a partition — never overlap. */
export function matchesStatus(entry, filter) {
  switch (filter) {
    case StatusFilter.INVOICE:
      return entry.isInvoice;
    case StatusFilter.PENDING:
      return !entry.isInvoice && entry.isPending;
    case StatusFilter.PAID:
      return !entry.isInvoice && !entry.isPending;
    case StatusFilter.ALL:
    default:
      return true;
  }
}

/** Free-text match across order number, customer, waiter and item names. */
export function matchesSearch(entry, query) {
  const q = query.trim().toLowerCase();
  if (!q) return true;

  if (String(entry.orderNumber).includes(q)) return true;
  if (entry.customerName.toLowerCase().includes(q)) return true;
  if (entry.staffName.toLowerCase().includes(q)) return true;

  const items = entry.order.items || [];
  return items.some((i) => String(i.name || '').toLowerCase().includes(q));
}

// ── Totals ─────────────────────────────────────────────────────────────────

/**
 * Money for a set of entries.
 *
 * `cash` deliberately EXCLUDES pending orders: a pending cash order is money
 * that is not in the drawer yet. `pending` counts any unsettled order,
 * invoice ones included, which is why the three slots do not add up to
 * `total` and are not meant to.
 */
export function computeTotals(entries) {
  const totals = entries.reduce(
    (acc, entry) => {
      acc.total += entry.total;
      acc.discount += entry.discount;

      if (entry.isInvoice) {
        acc.invoice += entry.total;
      } else if (!entry.isPending) {
        acc.cash += entry.total;

        /**
         * The same money again, split by where it landed.
         *
         * Only settled cash is counted, for the same reason `cash` is: a
         * pending order is not in anybody's account yet. An order settled
         * before `paymentAccount` existed has none, and is kept apart under
         * an empty key rather than being guessed into the drawer.
         */
        const key = entry.paymentAccount || '';
        const slot = acc.byAccount.get(key) || { label: key, amount: 0, count: 0 };
        slot.amount += entry.total;
        slot.count += 1;
        acc.byAccount.set(key, slot);
      }
      if (entry.isPending) acc.pending += entry.total;

      return acc;
    },
    { total: 0, cash: 0, invoice: 0, pending: 0, discount: 0, byAccount: new Map() }
  );

  /**
   * Biggest first, and the unnamed one last however big it is: it is history,
   * not an account somebody can pay into.
   */
  totals.accounts = Array.from(totals.byAccount.values()).sort((a, b) => {
    if (!a.label) return 1;
    if (!b.label) return -1;
    return b.amount - a.amount;
  });

  return totals;
}

// ── Grouping ───────────────────────────────────────────────────────────────

/** Entries arrive newest-first, so groups keep their order without a re-sort. */
export function groupByDay(entries) {
  const buckets = new Map();

  entries.forEach((entry) => {
    if (!buckets.has(entry.dayKey)) buckets.set(entry.dayKey, []);
    buckets.get(entry.dayKey).push(entry);
  });

  return Array.from(buckets.entries())
    .map(([dayKey, dayEntries]) => ({
      dayKey,
      date: moment(dayKey, 'YYYY-MM-DD'),
      entries: dayEntries,
      subtotal: dayEntries.reduce((sum, e) => sum + e.total, 0),
    }))
    .sort((a, b) => b.date.valueOf() - a.date.valueOf());
}

/** "Today" · "Yesterday · Thu 4 Sep" · "Wed, 3 Sep". */
export function dayLabel(date, now = moment()) {
  if (date.isSame(now, 'day')) return COPY.today;
  if (date.isSame(moment(now).subtract(1, 'day'), 'day')) {
    return `${COPY.yesterday} · ${date.format('ddd D MMM')}`;
  }
  return date.format('ddd, D MMM');
}

// ── Waiter options ─────────────────────────────────────────────────────────

function staffOptionsFrom(entries) {
  const byId = new Map();

  entries.forEach((entry) => {
    if (!entry.staffId || !entry.staffName) return;
    if (!byId.has(entry.staffId)) byId.set(entry.staffId, entry.staffName);
  });

  return Array.from(byId.entries())
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
}

// ── The one pass ───────────────────────────────────────────────────────────

/**
 * @param {Object}   input
 * @param {Array}    input.orders       raw orders for the loaded date range
 * @param {string}   input.statusFilter one of StatusFilter
 * @param {?string}  input.staffId      null means every waiter
 * @param {string}   input.search       free text, may be empty
 */
export function buildOrdersView({ orders, statusFilter, staffId, search }) {
  const scoped = (orders || [])
    .map(toEntry)
    .filter((entry) => !entry.isCanceled)
    .sort((a, b) => b.time.valueOf() - a.time.valueOf());

  // Options come from the scoped set, never the filtered one.
  const staff = staffOptionsFrom(scoped);

  // A waiter selection goes stale when the date range changes; drop it rather
  // than showing an empty list for a name that is no longer present.
  const activeStaffId = staff.some((o) => o.id === staffId) ? staffId : null;

  const byStaff = activeStaffId
    ? scoped.filter((entry) => entry.staffId === activeStaffId)
    : scoped;

  const searched = byStaff.filter((entry) => matchesSearch(entry, search));

  const visible =
    statusFilter === StatusFilter.ALL
      ? searched
      : searched.filter((entry) => matchesStatus(entry, statusFilter));

  return {
    staff,
    activeStaffId,

    /**
     * Every order in the range, by id. Lookups (the open panel, the cancel
     * dialog) go through this rather than `visible`, so flipping a chip does
     * not slam a panel shut on the order someone is reading.
     */
    byId: new Map(scoped.map((entry) => [entry.id, entry])),

    /**
     * Totals describe the RANGE, so they are computed before the status chip
     * is applied — tapping "Pending" must not zero out the Cash figure
     * sitting next to it. The list below is what the chip narrows.
     */
    summary: computeTotals(searched),
    summaryCount: searched.length,

    groups: groupByDay(visible),
    visible,
    isEmpty: visible.length === 0,

    /** True when something other than the date range is hiding orders. */
    hasNarrowingFilter:
      statusFilter !== StatusFilter.ALL || Boolean(activeStaffId) || Boolean(search.trim()),
  };
}
